import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Currency, FeatureFlag, FeatureFlagParam } from '@ps/web-core-enums';
import type { FeatureFlagRequestBody } from '@ps/web-core-store';
import { PromptTourType } from '@ps/web-core-store';
import { isDefined } from '@ps/web-core-utils';
import { addDays, format, isMatch, parseISO } from 'date-fns';
import { memoize, noop, omit } from 'lodash';
import type { FormatNumberOptions } from 'react-intl';
import type { Observable } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { expand, filter } from 'rxjs/operators';

import { Country, FormMethod } from '@swf/core/enums';
import type { FormatAsCurrency } from '@swf/core/hooks/useCurrencyFormatter';
import { dispatch, getState, select } from '@swf/core/store';
import { getPhoneNumberWithoutDialingCode } from '@swf/core/utils/phoneVerification';

import {
  creditTimeDays,
  creditTimeHours,
  creditTimeInstant,
  creditTimeMinutes,
  creditTimeTwoDays
} from '../components/Deposit/data';
import type {
  CashoutOptionRate,
  DepositPayload,
  DepositResult,
  Fields,
  FirstWizardValues,
  InovapayBankInfo,
  PaySystem,
  PaySystemDetails,
  PaySystems,
  PreCashinInfo,
  PsPayBank,
  Rates,
  RollbackOption,
  SavedCard,
  SavedCards
} from '../types/Cashier';
import { CashierFlowType } from '../types/Cashier';
import { CardType } from '../types/cardType';
import { DepositStatus, TransactionStatus } from '../types/status';

import {
  TXTCLI_Instant,
  TXTWCL_24_hours,
  TXTWCL_48_hours,
  TXTWCL_This_can_take_up_to_5_minutes__ELL,
  TXTWCL_Up_to_5_days
} from './cashier.messages.json';

export const conversionRateBase = 100000000;

export const dollarsToCents = (dollars: number): number => Number((dollars * 100).toFixed());

export const trimWithoutRounding = (value: number, decimalDigits = 2): number => {
  const v = Number(value || 0);
  if (v > Number.MAX_SAFE_INTEGER) {
    return v;
  }
  const numberToString = v.toFixed(10);
  return Number(numberToString.substring(0, numberToString.indexOf('.') + (decimalDigits + 1)));
};

export const zeroExponentCurrencies = [
  Currency.ChileanPeso,
  Currency.HungarianForint,
  Currency.JapaneseYen
];

export const isZeroExponentCurrency = memoize((currency: Currency) =>
  zeroExponentCurrencies.includes(currency)
);

export const convertAmountByRate = (
  amount: number,
  rate: number,
  currency: Currency,
  inverted = false,
  decimalDigits?: number
): number => {
  const trimmedAmount = trimWithoutRounding(amount);
  const result = Number(
    inverted
      ? (trimmedAmount * conversionRateBase) / rate
      : (trimmedAmount * rate) / conversionRateBase
  );

  return isZeroExponentCurrency(currency)
    ? Math.ceil(result)
    : trimWithoutRounding(result, decimalDigits);
};

export const formatExpiryDate = (expDate: number | undefined) => {
  if (expDate) {
    return isMatch(expDate.toString(), 'yyyyMM') ? expDate.toString() : format(expDate, 'yyyyMM');
  }
};

export const getCurrencyRate = (rates: Rates | CashoutOptionRate[], currency: Currency) => {
  const currencyRate = rates.find(({ curr }) => curr === currency);
  return currencyRate?.rate || -1;
};

export const sanitizeDeposit = (payload: DepositPayload) =>
  omit(payload, ['accountNumber', 'cvn', 'expiryDate']);

export const getPaySystemName = (paySystems: PaySystems, type: CardType) => {
  let paySystemName = '';
  const paySystem = paySystems.find(
    ({ cardType }: { cardType: CardType }) => cardType === type
  ) as PaySystem;
  if (paySystem) {
    const { i18nSystemName = '' } = paySystem;
    paySystemName = i18nSystemName;
  }
  return paySystemName;
};

export const getAsPayBankName = (asPaybankInfo: PsPayBank[], selectedBankId: number) => {
  let asPayBankName = '';
  let asPayBankAccountNumber = '';
  const bank = asPaybankInfo.find(({ bankId }: { bankId: number }) => bankId === selectedBankId);
  if (bank) {
    const { bankName = '', bankAccount } = bank;
    asPayBankName = bankName;
    asPayBankAccountNumber = bankAccount;
  }
  return { asPayBankName, asPayBankAccountNumber };
};

export const getBankDetails = (
  selectedProductId: string,
  allowedSubsystems?: InovapayBankInfo[],
  savedMethod?: SavedCard
) => {
  const bankName = allowedSubsystems?.find(
    ({ productId }) => productId === selectedProductId
  )?.description;
  const savedMaskedNumber =
    savedMethod && savedMethod.savedCard.maskedCardNum ? savedMethod.savedCard.maskedCardNum : '';

  return { bankName, savedMaskedNumber };
};

export const getSavedMethod = (savedCards: SavedCards, savedCardInfoId?: number): SavedCard[] =>
  savedCards.filter(({ savedCard: { cardInfoId } }) => cardInfoId === savedCardInfoId);

export const getDepositStatus = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.APPROVED:
      return DepositStatus.COMPLETED;
    case TransactionStatus.REJECTED:
      return DepositStatus.REJECTED;
    default:
      return DepositStatus.TIMEOUT;
  }
};

export const getRmfRequiredPath = (
  site: Sites,
  isSignUpOnePageEnabled: boolean,
  isUSPAEnhancedFlow: boolean,
  country?: string
) => {
  if (isUSPAEnhancedFlow) {
    return '#rmf.rmf-phone';
  }

  if (isSignUpOnePageEnabled && ![Sites.ITA, Sites.UK].includes(site)) {
    return '#rmf.rmfdob';
  }

  const path: Partial<Record<Sites | string, string>> = {
    [Country.Brazil]: '#rmf.sum-sub-first'
  } as const;

  return (country && path[country]) || '#rmf.signup-dob-fetch';
};

export const trimAsterisks = (text: string) => text.replace(/\*{4,}/g, '****');

export const filterPaySystems = (
  paySystems: PaySystems,
  isApplePayAvailable: boolean,
  isGooglePayDepositAvailable: boolean
) =>
  paySystems.filter(({ cardType }) => {
    if (cardType === CardType.ApplePay) {
      return isApplePayAvailable;
    }
    if (cardType === CardType.GooglePay) {
      return isGooglePayDepositAvailable;
    }

    return true;
  });

export const getCreditTime = (type: CardType, country?: Country, depositSuccess?: boolean) => {
  if (type === CardType.PsPayAstropay && country) {
    switch (country) {
      case Country.Peru:
      case Country.Uruguay:
        return TXTWCL_24_hours;
      case Country.Mexico:
      case Country.Brazil:
        return TXTWCL_48_hours;
      case Country.Chile:
        return depositSuccess ? TXTWCL_This_can_take_up_to_5_minutes__ELL : TXTCLI_Instant;
    }
  }
  if (creditTimeInstant.includes(type)) {
    return TXTCLI_Instant;
  }
  if (creditTimeMinutes.includes(type)) {
    return depositSuccess ? TXTWCL_This_can_take_up_to_5_minutes__ELL : TXTCLI_Instant;
  }
  if (creditTimeDays.includes(type)) {
    return TXTWCL_Up_to_5_days;
  }
  if (creditTimeHours.includes(type)) {
    return TXTWCL_24_hours;
  }
  if (creditTimeTwoDays.includes(type)) {
    return TXTWCL_48_hours;
  }
};

export const formatLimitOptions = (
  currency: Currency,
  options: number[],
  formatAsCurrency: FormatAsCurrency
) =>
  options.map((value) => ({
    value,
    text: formatAsCurrency({ value, currency, minimumFractionDigits: 0, cents: true })
  }));

export const executeHistoryPush = () => {
  if (!window.history.state?.prevUrl) {
    window.history.pushState(
      { prevUrl: window.location.href },
      document.title,
      window.location.href
    );
  }
};

export const getClosetConvertableAmount = (
  rate: number,
  side: 'left' | 'right',
  sourceCurrency: Currency,
  targetAmount: number,
  targetCurrency: Currency
): Observable<number> => {
  const calculatedSourceAmount = convertAmountByRate(targetAmount, rate, targetCurrency, true, 2);
  if (side === 'left') {
    const condition = (res: number) =>
      convertAmountByRate(res, rate, sourceCurrency, false, 2) <= +targetAmount;

    return of(calculatedSourceAmount).pipe(
      expand((val) => (condition(val) ? EMPTY : of(+(val - 0.01).toFixed(2)))),
      filter(condition)
    );
  } else {
    const condition = (res: number) =>
      convertAmountByRate(res, rate, sourceCurrency, false, 2) >= +targetAmount;

    return of(calculatedSourceAmount).pipe(
      expand((val) => (condition(val) ? EMPTY : of(+(val + 0.01).toFixed(2)))),
      filter(condition)
    );
  }
};

export const getConversionRate = (rates: Rates, currency: Currency) =>
  rates.reduce((result, { curr, rate }) => {
    if (curr === currency) {
      result = rate;
    }
    return result;
  }, conversionRateBase);

export const isRedirectStatus = (status: DepositStatus) =>
  [
    DepositStatus.REDIRECT,
    DepositStatus.REJECTED_X3D_SECURE2_REDIRECT,
    DepositStatus.REJECTED_X3D_SECURE_REQUIRED_ERROR,
    DepositStatus.REJECTED_X3D_SECURE2_REQUIRED_ERROR
  ].includes(status);

const processFormFields = (values: FirstWizardValues) => {
  if (isDefined(values.docIssueDate)) {
    values.docIssueDate = format(parseISO(values.docIssueDate), 'yyyy-LL-dd'); // date format is defined in IssueDateField
  }
  if (isDefined(values.intentToPlay) && values.intentToPlay === 0) {
    delete values.intentToPlay;
  }
  if (isDefined(values.occupationCompanyPhone) && isDefined(values.country)) {
    values.occupationDialingCode = values.country;
    values.occupationCompanyPhone = getPhoneNumberWithoutDialingCode(
      values.occupationCompanyPhone,
      values.country
    );
  }
};

export const updateFormFieldsValues = (values: FirstWizardValues, fieldsData: Fields = []) => {
  fieldsData.forEach(({ name, value }) => {
    if (!!value || value === 0) {
      (values[name as keyof FirstWizardValues] as FirstWizardValues[keyof FirstWizardValues]) =
        value;
    }
  });
  processFormFields(values);
};

export const calculateCashBonusAmount = (
  preCashinInfo: PreCashinInfo | null,
  formatNumber: (value: number | bigint, opts?: FormatNumberOptions | undefined) => string
): string | undefined => {
  if (!preCashinInfo) return;

  const {
    fppToExercise,
    bonusMax,
    bonusPercent,
    potentialBonusAmount,
    instantBonusInfo,
    bonusCurrency
  } = preCashinInfo;

  if (!instantBonusInfo && fppToExercise === 0 && !!(bonusMax || bonusPercent)) {
    return formatNumber(Math.min(bonusMax, potentialBonusAmount), {
      currency: bonusCurrency,
      style: 'currency',
      currencyDisplay: 'code',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
  }

  return;
};

export const formatCardReference = (ref?: string): string => {
  if (!ref) {
    return '';
  }

  if (ref.includes('@')) {
    const [localPart, domainPart] = ref.split('@');
    let maskedLocalPart = localPart;
    if (localPart.length > 2) {
      maskedLocalPart = `${localPart[0]}${'*'.repeat(localPart.length - 1)}`;
    } else {
      maskedLocalPart = '*';
    }

    maskedLocalPart = trimAsterisks(maskedLocalPart);

    return `${maskedLocalPart}@${domainPart}`;
  }

  return `${ref[0]}****${ref.slice(-4)}`;
};

export const getPaymentVerificationPath = (
  cashierFlow: CashierFlowType,
  showPaymentVerificationLobby?: boolean
) => {
  const path: Partial<Record<CashierFlowType, string>> = {
    [CashierFlowType.FirstDeposit]: '#payment-verification.payment-verification-menu',
    [CashierFlowType.FirstWithdraw]: showPaymentVerificationLobby
      ? '#payment-verification.payment-verification-lobby'
      : '#payment-verification.payment-verification-page'
  } as const;

  return path[cashierFlow] as string;
};

export const detailsForceSavedMethods = (type: CardType) =>
  [CardType.ECheck, CardType.Pix, CardType.Skrill1Tap].includes(type);

export const filteredUnverifiedOptions = (
  unverifiedOptions: RollbackOption[],
  i18nSystemName: string
) => unverifiedOptions.filter((option) => option.paySystemName === i18nSystemName);

export const getNextDaysTimestamp = (currentDate: Date, days: number) =>
  addDays(currentDate, days).getTime();

export const checkCesFormExpirationStatus = (еxpireDate: number) => {
  if (!еxpireDate) return true;
  const currentDate = new Date();
  const currentDateTimestamp = currentDate.getTime();

  return currentDateTimestamp > еxpireDate;
};

export const mapCashierFlowToPromptTourType = (
  cashierFlow: CashierFlowType
): PromptTourType | undefined => {
  switch (cashierFlow) {
    case CashierFlowType.FirstDeposit:
      return PromptTourType.FirstDeposit;
    case CashierFlowType.FirstWithdraw:
      return PromptTourType.FirstWithdraw;
    default:
      return undefined;
  }
};

export const stripNonDigits = (value: string) => value.replace(/[^0-9]/g, '');

export const checkP2pTransferFlag = async () => {
  const rootState = getState();
  const fetchVipPoints = select.feature.check(rootState, FeatureFlag.SwcFetchVipPoints);

  const query: FeatureFlagRequestBody['query'] = {};

  if (fetchVipPoints) {
    const response = await dispatch.account.fetchVipPoints();
    query[FeatureFlagParam.MinLtVpp] = response?.ltVppInCents || 0;
  }
  await dispatch.feature.check({ features: [FeatureFlag.SwcP2pTransfer], query }).catch(noop);
  return select.feature.check(getState(), FeatureFlag.SwcP2pTransfer);
};

export const verifyEmail = async (emailVerificationCode: string) => {
  try {
    const response = await dispatch.account.validateEmailCode({
      code: +emailVerificationCode
    });

    if (response?.statusCode) {
      return Promise.reject({
        body: {
          globalErrors: [],
          fieldErrors: [
            {
              code: 'TXTWCL_Invalid_codeX_Please_try_again_ELL',
              fieldName: 'emailVerificationCode'
            }
          ]
        }
      });
    }

    return Promise.resolve({ emailCodeStatus: response?.success });
  } catch {
    return Promise.reject({
      body: {}
    });
  }
};

export const generateDepositResult = (
  status: DepositStatus,
  paySystemDetails: PaySystemDetails
): DepositResult => ({
  bonusId: 0,
  depositSuggestions: null,
  info: null,
  message: {
    code: null,
    arguments: null,
    defaultMessage: null
  },
  paySystemDetails,
  plaidLinkToken: null,
  realMoneyInformation: null,
  redirectUrl: {
    url: '',
    method: FormMethod.Get
  },
  repeatedCashinDeclineOptions: null,
  status,
  systemFee: null,
  transactionId: '',
  trhash: '',
  unusedLossLimit: null,
  url: '',
  paymentRefId: '',
  expiryTime: '',
  entityId: '',
  resultInfo: null,
  processor: null,
  preCashinInfo: null
});
